import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createPageUrl } from '../utils/index.ts';
import { Project } from '../api/entities.js';
import { Company } from '../api/entities.js';
import { User } from '../api/entities.js';
import { Button } from "../components/ui/button.jsx";
import { Input } from "../components/ui/input.jsx";
import { Textarea } from "../components/ui/textarea.jsx";
import { Label } from "../components/ui/label.jsx";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card.jsx";
import { Badge } from "../components/ui/badge.jsx";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar.jsx";
import { Progress } from "../components/ui/progress.jsx";
import { Separator } from "../components/ui/separator.jsx";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select.jsx";
import { 
  ArrowLeft, 
  Save, 
  Calendar, 
  Building2, 
  Users, 
  User as UserIcon,
  CheckCircle2,
  Clock,
  PlayCircle,
  PauseCircle,
  Lightbulb,
  AlertTriangle,
  FileText,
  Settings,
  Target,
  X,
  Plus
} from 'lucide-react';
import { format, parse } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../components/ui/tooltip.jsx";

const STATUS_CONFIG = {
  planning: {
    label: 'Planejamento',
    icon: Lightbulb,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200'
  },
  in_progress: {
    label: 'Em Andamento',
    icon: PlayCircle,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200'
  },
  paused: {
    label: 'Pausado',
    icon: PauseCircle,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200'
  },
  completed: {
    label: 'Concluído',
    icon: CheckCircle2,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200'
  }
};

export default function EditProject() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('id');
  const isEditing = !!projectId;

  // Dados para formulário
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTeamMembers, setSelectedTeamMembers] = useState([]);

  // Dados do projeto
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    client_id: '',
    status: 'planning',
    start_date: format(new Date(), 'yyyy-MM-dd'),
    end_date: '',
    manager_id: '',
    team: []
  });

  // Validação
  const [validationErrors, setValidationErrors] = useState({});

  // Carregar dados
  useEffect(() => {
    const loadFormData = async () => {
      setLoading(true);
      try {
        const [companiesData, usersData] = await Promise.all([
          Company.list(),
          User.list()
        ]);
        
        setCompanies(companiesData);
        setUsers(usersData);

        if (isEditing) {
          const projectData = await Project.findById(projectId);
          
          const formattedData = {
            ...projectData,
            start_date: projectData.start_date ? format(new Date(projectData.start_date), 'yyyy-MM-dd') : '',
            end_date: projectData.end_date ? format(new Date(projectData.end_date), 'yyyy-MM-dd') : '',
            team: projectData.team || [],
            manager_id: projectData.manager_id || ''
          };
          
          setFormData(formattedData);
          
          // Configurar membros selecionados
          if (projectData.team) {
            const teamMembers = usersData.filter(user => 
              projectData.team.includes(user.email)
            );
            setSelectedTeamMembers(teamMembers);
          }
        }
      } catch (error) {
        console.error('Error loading form data:', error);
        setError('Erro ao carregar dados do formulário.');
      }
      setLoading(false);
    };

    loadFormData();
  }, [projectId, isEditing]);

  // Validação do formulário
  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Nome do projeto é obrigatório';
    }
    
    if (!formData.client_id) {
      errors.client_id = 'Cliente é obrigatório';
    }
    
    if (!formData.start_date) {
      errors.start_date = 'Data de início é obrigatória';
    }
    
    if (formData.end_date && new Date(formData.end_date) < new Date(formData.start_date)) {
      errors.end_date = 'Data de término deve ser posterior à data de início';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpar erro de validação quando o usuário corrige
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const addTeamMember = (userId) => {
    const user = users.find(u => u.id === userId);
    if (user && !selectedTeamMembers.find(m => m.id === userId)) {
      const newMembers = [...selectedTeamMembers, user];
      setSelectedTeamMembers(newMembers);
      setFormData(prev => ({
        ...prev,
        team: newMembers.map(m => m.email)
      }));
    }
  };

  const removeTeamMember = (userId) => {
    const newMembers = selectedTeamMembers.filter(m => m.id !== userId);
    setSelectedTeamMembers(newMembers);
    setFormData(prev => ({
      ...prev,
      team: newMembers.map(m => m.email)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSaving(true);
    setError(null);
    
    try {
      const projectData = {
        ...formData,
        start_date: formData.start_date,
        end_date: formData.end_date || null
      };
      
      if (isEditing) {
        await Project.update(projectId, projectData);
      } else {
        await Project.create(projectData);
      }
      
      navigate(createPageUrl('Projects'));
    } catch (error) {
      console.error('Error saving project:', error);
      setError(error.message || 'Erro ao salvar o projeto.');
    }
    
    setSaving(false);
  };

  const calculateProgress = () => {
    if (formData.status === 'completed') return 100;
    if (formData.status === 'in_progress') return 60;
    if (formData.status === 'planning') return 20;
    if (formData.status === 'paused') return 40;
    return 0;
  };

  const getDaysRemaining = () => {
    if (!formData.end_date) return null;
    const end = new Date(formData.end_date);
    const today = new Date();
    const diffTime = end - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getSelectedClient = () => {
    return companies.find(c => c.id === formData.client_id);
  };

  const getSelectedManager = () => {
    return users.find(u => u.id === formData.manager_id);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
          <p className="text-muted-foreground">Carregando dados do projeto...</p>
        </div>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[formData.status];
  const progress = calculateProgress();
  const daysRemaining = getDaysRemaining();
  const selectedClient = getSelectedClient();
  const selectedManager = getSelectedManager();

  return (
    <div className="space-y-6">
      {/* Header moderno */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-6 text-white"
      >
        <div className="flex items-center gap-4">
          <Button
            variant="secondary"
            size="icon"
            onClick={() => navigate(createPageUrl('Projects'))}
            className="bg-white/20 hover:bg-white/30 text-white border-white/30"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold mb-2">
              {isEditing ? 'Editar Projeto' : 'Novo Projeto'}
            </h1>
            <p className="text-blue-100 text-lg">
              {isEditing ? 'Modifique os dados do projeto' : 'Preencha os dados para criar um novo projeto'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={handleSubmit}
              disabled={saving}
              className="bg-white text-blue-600 hover:bg-white/90 font-medium"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Salvando...' : isEditing ? 'Atualizar' : 'Criar'}
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Preview do projeto (se editando) */}
      {isEditing && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className={`${statusConfig.bgColor} ${statusConfig.borderColor} border-2`}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg bg-white/80`}>
                    <statusConfig.icon className={`w-5 h-5 ${statusConfig.color}`} />
                  </div>
                  <div>
                    <CardTitle className="text-lg">{formData.name || 'Nome do projeto'}</CardTitle>
                    <Badge variant="secondary" className={`${statusConfig.color} ${statusConfig.bgColor} mt-1`}>
                      {statusConfig.label}
                    </Badge>
                  </div>
                </div>
                {selectedClient && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Building2 className="w-4 h-4" />
                    <span>{selectedClient.corporate_name || selectedClient.name}</span>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Período</p>
                    <p className="text-sm text-muted-foreground">
                      {formData.start_date ? format(new Date(formData.start_date), "dd/MM/yyyy", { locale: ptBR }) : 'Não definido'}
                      {formData.end_date && ` - ${format(new Date(formData.end_date), "dd/MM/yyyy", { locale: ptBR })}`}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Progresso</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Progress value={progress} className="h-2 flex-1" />
                      <span className="text-sm font-medium">{progress}%</span>
                    </div>
                  </div>
                </div>

                {daysRemaining !== null && (
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Prazo</p>
                      <p className={`text-sm font-medium ${
                        daysRemaining < 0 ? 'text-red-600' : 
                        daysRemaining <= 7 ? 'text-orange-600' : 'text-green-600'
                      }`}>
                        {daysRemaining < 0 ? 
                          `${Math.abs(daysRemaining)} dias atrasado` :
                          `${daysRemaining} dias restantes`
                        }
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Mensagem de erro */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg"
          >
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              <span>{error}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Formulário organizado em seções */}
      <form onSubmit={handleSubmit} className="space-y-6">
        
        {/* Seção: Informações Básicas */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Informações Básicas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome do Projeto *</Label>
                  <Input 
                    id="name" 
                    value={formData.name} 
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Digite o nome do projeto"
                    className={validationErrors.name ? 'border-red-500' : ''}
                  />
                  {validationErrors.name && (
                    <p className="text-sm text-red-600">{validationErrors.name}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="client_id">Cliente *</Label>
                  <Select value={formData.client_id} onValueChange={(value) => handleInputChange('client_id', value)}>
                    <SelectTrigger className={validationErrors.client_id ? 'border-red-500' : ''}>
                      <SelectValue placeholder="Selecione um cliente" />
                    </SelectTrigger>
                    <SelectContent>
                      {companies.map(company => (
                        <SelectItem key={company.id} value={company.id}>
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4" />
                            {company.corporate_name || company.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {validationErrors.client_id && (
                    <p className="text-sm text-red-600">{validationErrors.client_id}</p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Descrição</Label>
                <Textarea 
                  id="description" 
                  value={formData.description} 
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Descreva o projeto, objetivos e escopo"
                  rows={4}
                />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Seção: Status e Cronograma */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Status e Cronograma
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-6 md:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="status">Status do Projeto</Label>
                  <Select value={formData.status} onValueChange={(value) => handleInputChange('status', value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                        <SelectItem key={key} value={key}>
                          <div className="flex items-center gap-2">
                            <config.icon className={`w-4 h-4 ${config.color}`} />
                            {config.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="start_date">Data de Início *</Label>
                  <Input 
                    type="date" 
                    id="start_date" 
                    value={formData.start_date} 
                    onChange={(e) => handleInputChange('start_date', e.target.value)}
                    className={validationErrors.start_date ? 'border-red-500' : ''}
                  />
                  {validationErrors.start_date && (
                    <p className="text-sm text-red-600">{validationErrors.start_date}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="end_date">Data de Término</Label>
                  <Input 
                    type="date" 
                    id="end_date" 
                    value={formData.end_date} 
                    onChange={(e) => handleInputChange('end_date', e.target.value)}
                    className={validationErrors.end_date ? 'border-red-500' : ''}
                  />
                  {validationErrors.end_date && (
                    <p className="text-sm text-red-600">{validationErrors.end_date}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Seção: Equipe */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Equipe do Projeto
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="manager_id">Gestor do Projeto</Label>
                  <Select value={formData.manager_id} onValueChange={(value) => handleInputChange('manager_id', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione um gestor" />
                    </SelectTrigger>
                    <SelectContent>
                      {users
                        .filter(user => user.role !== 'client')
                        .map(user => (
                          <SelectItem key={user.id} value={user.id}>
                            <div className="flex items-center gap-2">
                              <UserIcon className="w-4 h-4" />
                              {user.name || user.full_name}
                            </div>
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  {selectedManager && (
                    <div className="flex items-center gap-2 mt-2 p-2 bg-muted rounded">
                      <Avatar className="h-6 w-6">
                        <AvatarFallback className="text-xs">
                          {(selectedManager.name || selectedManager.full_name || 'U').substring(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm">{selectedManager.name || selectedManager.full_name}</span>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Adicionar Membro da Equipe</Label>
                  <Select onValueChange={addTeamMember}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione um membro" />
                    </SelectTrigger>
                    <SelectContent>
                      {users
                        .filter(user => user.role !== 'client' && !selectedTeamMembers.find(m => m.id === user.id))
                        .map(user => (
                          <SelectItem key={user.id} value={user.id}>
                            <div className="flex items-center gap-2">
                              <UserIcon className="w-4 h-4" />
                              {user.name || user.full_name}
                            </div>
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Lista de membros selecionados */}
              {selectedTeamMembers.length > 0 && (
                <div className="space-y-2">
                  <Label>Membros da Equipe ({selectedTeamMembers.length})</Label>
                  <div className="grid gap-2">
                    <AnimatePresence>
                      {selectedTeamMembers.map(member => (
                        <motion.div
                          key={member.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          className="flex items-center justify-between p-3 bg-muted rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <Avatar className="h-8 w-8">
                              <AvatarFallback className="text-xs">
                                {(member.name || member.full_name || 'U').substring(0, 2).toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium text-sm">{member.name || member.full_name}</p>
                              <p className="text-xs text-muted-foreground">{member.email}</p>
                            </div>
                          </div>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => removeTeamMember(member.id)}
                                className="h-8 w-8 text-red-500 hover:text-red-600"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Remover membro</TooltipContent>
                          </Tooltip>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Botões de ação */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="flex justify-end gap-3 pt-6"
        >
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(createPageUrl('Projects'))}
            className="min-w-[120px]"
          >
            Cancelar
          </Button>
          <Button 
            type="submit" 
            disabled={saving}
            className="min-w-[120px]"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Salvando...' : isEditing ? 'Atualizar' : 'Criar'}
          </Button>
        </motion.div>
      </form>
    </div>
  );
}